/**
 * QtyStepper — canonical ADD → [- 1 +] control used everywhere.
 * Reads live qty from CartContext and drives changes through it, so the same
 * product’s stepper stays in sync across Home / Shop / Search / PDP / Cart /
 * Wishlist. Behaviour is centralised — no page duplicates its own logic.
 */
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useStore } from "@/contexts/StoreContext";
import { Minus, Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { errMsg } from "@/lib/api";
import { flyToCart } from "@/lib/flyToCart";

export default function QtyStepper({
  product,                       // full product row (needs id, name)
  variantId = null,              // optional variant id — enables variant-aware cart lines
  size = "sm",                   // 'sm' | 'md' | 'lg'
  fullWidth = false,
  className = "",
  style = null,                  // optional inline style forwarded to the outer element
  onAdded,                       // optional callback after +1
  testIdPrefix = "qty",          // build data-testids from this + product.id
  addLabel = "ADD",
}) {
  const { user } = useAuth();
  const { qtyOf, changeQty } = useCart() || { qtyOf: () => 0, changeQty: async () => {} };
  const { canOrder, isOpen, isServiceable, closedReason } = useStore();
  const nav = useNavigate();
  const qty = qtyOf(product.id, variantId);
  const moq = Math.max(1, Number(product.moq || 1));               // Sprint 15 #7
  const maxQty = Number(product.max_order_qty || 0) > 0
    ? Math.max(1, Number(product.max_order_qty))
    : Infinity;                                                     // Sprint 59 — per-order cap

  const change = async (delta, sourceEl) => {
    if (!canOrder) {
      toast.info(!isOpen ? (closedReason || "Store is currently closed.") : "Delivery unavailable at your location");
      return;
    }
    // Sprint 58 — fly-to-cart animation on positive ADD. Fires immediately so it
    // overlaps with the optimistic cart update; the server round-trip runs behind.
    if (delta > 0 && sourceEl) {
      const imgUrl = product.thumbnail_url || product.image || (Array.isArray(product.images) ? product.images[0] : null);
      flyToCart({ sourceEl, imgUrl });
    }
    try {
      // Enforce MOQ: first add jumps straight to `moq` qty; decrements below moq remove the line.
      let effectiveDelta = delta;
      if (delta > 0 && qty === 0 && moq > 1) effectiveDelta = moq;
      if (delta < 0 && qty > 0 && qty - 1 < moq) effectiveDelta = -qty;         // drop the whole line
      // Sprint 59 — enforce max_order_qty per order.
      if (delta > 0 && qty + effectiveDelta > maxQty) {
        toast.info(`Max ${maxQty} allowed per order for ${product.name}`);
        return;
      }
      // Pass the full product snapshot — CartContext uses it as the seed for new
      // guest-cart lines (localStorage-backed). Signed-in customers ignore it.
      await changeQty(product.id, effectiveDelta, variantId, product);
      if (effectiveDelta > 0) {
        onAdded?.(product);
      }
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Sizing scale
  const dims = size === "lg"
    ? { btn: "h-10 px-4 text-sm", stepBtn: "h-10 w-10", num: "min-w-[3rem] text-base" }
    : size === "md"
    ? { btn: "h-9 px-3.5 text-sm", stepBtn: "h-9 w-9", num: "min-w-[2.5rem] text-sm" }
    : { btn: "h-8 px-2 text-[13px]", stepBtn: "h-8 w-6 shrink-0", num: "min-w-[1.2rem] text-center text-[12px]" };

  // Store closed / unserviceable — disable but keep visible
  if (!canOrder) {
    return (
      <button
        disabled
        onClick={(e) => e.preventDefault()}
        className={`${dims.btn} rounded-lg inline-flex items-center gap-1 border border-border bg-muted text-muted-foreground font-semibold cursor-not-allowed ${fullWidth ? "w-full justify-center" : ""} ${className}`}
        style={style || undefined}
        data-testid={`${testIdPrefix}-closed-${product.id}`}
        title={closedReason || "Unavailable at this location"}
      >
        <Lock className="w-3.5 h-3.5" />
        {!isOpen ? "Closed" : "N/A"}
      </button>
    );
  }

  if (qty === 0) {
    // Sprint 57 — the style prop can carry background/borderColor/color for the ADD state.
    // Only apply solid color classes when style doesn't already override.
    const hasCustomBg     = style && (style.background || style.backgroundColor);
    const hasCustomBorder = style && style.borderColor;
    const hasCustomText   = style && style.color;
    // Blinkit-style: default ADD button is GREEN OUTLINE pill. Only fall back
    // to red-primary defaults when a `style` prop explicitly overrides colors.
    const useBlinkitGreen = !hasCustomBg && !hasCustomBorder && !hasCustomText;
    return (
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); change(1, e.currentTarget); }}
        className={`${dims.btn} inline-flex items-center justify-center border font-bold tracking-wide transition press ${
          useBlinkitGreen ? "bb-add-btn" : `${hasCustomBorder ? "" : "border-primary/40"} ${hasCustomBg ? "" : "bg-primary/5 hover:bg-primary hover:text-primary-foreground"} ${hasCustomText ? "" : "text-primary"} hover:brightness-95`
        } ${fullWidth ? "w-full" : ""} ${className}`}
        style={style || { borderRadius: "0.5rem" }}
        data-testid={`${testIdPrefix}-add-${product.id}`}
      >
        {addLabel}
      </button>
    );
  }

  // Blinkit-style: default stepper is GREEN filled. Overridden only if a `style` prop
  // explicitly sets a background.
  const stepperHasCustomBg = style && (style.background || style.backgroundColor);
  return (
    <div
      className={`inline-flex items-center rounded-lg overflow-hidden shadow-sm ${
        stepperHasCustomBg ? "" : "bb-stepper-green"
      } ${fullWidth ? "w-full justify-between" : ""} ${className}`}
      style={style || undefined}
      onClick={(e) => e.preventDefault()}
      data-testid={`${testIdPrefix}-stepper-${product.id}`}
    >
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); change(-1, e.currentTarget); }}
        className={`${dims.stepBtn} grid place-items-center hover:bg-white/20 transition press`}
        aria-label="Decrease"
        data-testid={`${testIdPrefix}-dec-${product.id}`}
      >
        <Minus className="w-4 h-4" />
      </button>
      <div className={`${dims.num} text-center font-mono font-semibold`} data-testid={`${testIdPrefix}-val-${product.id}`}>{qty}</div>
      <button
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); change(1, e.currentTarget); }}
        className={`${dims.stepBtn} grid place-items-center hover:bg-white/20 transition press`}
        aria-label="Increase"
        data-testid={`${testIdPrefix}-inc-${product.id}`}
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}
